import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocale } from '../i18n/LocaleContext';
import type { VisualThemeId } from '../theme/visualThemeTypes';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { controlPill, controlTextM } from '../theme/controlMetrics';
import { fillTemplate } from '../util/fillTemplate';
import { AvatarBadge } from './AvatarBadge';
import { ComposeGlyph, MoreGlyph, VideoCameraGlyph } from './ChatListGlyphs';
import { TintedIcon } from './TintedIcon';
import { UserPhotoViewer } from './UserPhotoViewer';

const PHONE_ICON = require('../../assets/chat-icons/phone.png');
/** Красный разрушающих действий — один на все темы, как системный. */
const DANGER = '#dc2626';

/**
 * Круглая кнопка ряда «Написать / Позвонить / Видео» с подписью под ней. Подпись — часть
 * кнопки, а не соседний текст: зона нажатия 56 dp и для пальца, и для экранного диктора.
 */
function QuickAction({
  label,
  color,
  onPress,
  children,
}: {
  label: string;
  color: string;
  onPress: () => void;
  children: React.ReactNode;
}) {
  const { loginChrome } = useVisualTheme();
  return (
    <Pressable
      style={({ pressed }) => [styles.quickAction, pressed && { opacity: 0.6 }]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}>
      <View style={[styles.quickCircle, { backgroundColor: color }]}>{children}</View>
      <Text style={[styles.quickLabel, { color: loginChrome.titleColor }]} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

export type UserAvatarPreviewTarget = {
  userId: string;
  displayName: string;
  hasAvatar?: boolean | null;
  revision?: number;
  shapeId?: string | null;
  publicHandle?: string | null;
  bio?: string | null;
  statusText?: string | null;
  /**
   * Пол и возраст (заказ владельца 2026-08-29). Оба необязательны и оба показываются только
   * если человек их указал: пустая строка «Пол: не указан» в чужой карточке — это сообщение
   * о том, чего нет.
   */
  gender?: 'MALE' | 'FEMALE' | null;
  age?: number | null;
  /** День и месяц рождения без года — для строки «14 марта». */
  birthDay?: number | null;
  birthMonth?: number | null;
  /** If provided, a primary "Write" button is shown */
  onWriteMessage?: () => void;
  /** If provided, an "Add contact" button is shown */
  onAddContact?: () => void;
  /** When true, shows "Request sent" instead of "Add contact" */
  contactPending?: boolean;
  /**
   * If provided, a "Share contact" action is shown. Заполняется только для друга
   * с открытым профилем — у «защищённого» аккаунта делиться нельзя (стори 5.11).
   */
  onShareContact?: () => void;
  /**
   * Можно ли открыть фотографию во весь экран. Ставит его тот, кто открывает карточку:
   * только он знает, друг перед нами или найденный поиском незнакомец.
   *
   * <p>Право смотреть крупно — у друзей и у себя самого. Маленький бейдж в списке
   * показывает, с кем говоришь; разглядывать снимок целиком — уже другая близость, и
   * человек, добавляя друга, соглашается именно на неё. Чужим аватар остаётся кружком
   * в 120 px, как и был.
   */
  canViewPhoto?: boolean;
  /**
   * Карточка как профиль (вариант A аудита 2026-10-04): «Написать / Позвонить / Видео» в один
   * ряд, остальное — в «⋯». Каждое действие появляется, только если его передали: незнакомцу
   * из поиска звонить нельзя, и кнопка, которая отвечает отказом, хуже отсутствующей.
   */
  onCall?: () => void;
  onVideoCall?: () => void;
  /** Поделиться ссылкой на профиль системным «Поделиться». Есть только у человека с ником. */
  onShareLink?: () => void;
  /** Удалить из контактов — с подтверждением прямо в карточке. */
  onRemoveContact?: () => Promise<void> | void;
  /** Заблокировать — с подтверждением прямо в карточке. */
  onBlock?: () => Promise<void> | void;
  /**
   * Кем человек приходится, узнать не удалось (офлайн): кнопок отношения нет, а эта строка
   * объясняет почему (аудит 2026-10-17, UX-57).
   */
  relationNote?: string | null;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  target: UserAvatarPreviewTarget | null;
  /**
   * 'overlay' — карточка рисуется слоем на месте, без <Modal>. Нужно везде, где
   * её открывают из уже открытой модалки: второй одновременно показанный
   * RN-Modal на iOS подвешивает презентацию (экран перестаёт отвечать вообще),
   * на Android уезжает под первый. Вне модалок остаётся 'modal' — системное
   * окно, которое умеет закрываться аппаратной кнопкой «назад».
   */
  presentation?: 'modal' | 'overlay';
};

function modalOverlayRgba(visualTheme: VisualThemeId): string {
  if (visualTheme === 'dark') return 'rgba(0, 0, 0, 0.78)';
  if (visualTheme === 'varangian') return 'rgba(12, 25, 36, 0.68)';
  return 'rgba(15, 23, 42, 0.52)';
}

export function UserAvatarPreviewModal({
  visible,
  onClose,
  target,
  presentation = 'modal',
}: Props): React.ReactElement | null {
  const { t } = useLocale();
  const insets = useSafeAreaInsets();
  const { visualTheme, loginChrome } = useVisualTheme();

  const [photoOpen, setPhotoOpen] = useState(false);
  /** Показан ли ответ на касание чужого фото — «крупно смотрят друзья». */
  const [friendsOnlyHint, setFriendsOnlyHint] = useState(false);
  /** Раскрыт ли список «⋯». */
  const [moreOpen, setMoreOpen] = useState(false);
  /**
   * Какое разрушающее действие ждёт подтверждения. Вопрос задаётся внутри карточки, а не
   * отдельным окном: второе системное окно поверх карточки на iOS подвешивает экран, на
   * Android уходит под неё (см. `presentation`).
   */
  const [confirming, setConfirming] = useState<'remove' | 'block' | null>(null);
  const [busy, setBusy] = useState(false);

  const targetUserId = target?.userId ?? null;
  // Карточку переоткрывают на другом человеке, не размонтируя, — сбрасываем всё «своё».
  const [shownFor, setShownFor] = useState({ targetUserId, visible });
  if (shownFor.targetUserId !== targetUserId || shownFor.visible !== visible) {
    setShownFor({ targetUserId, visible });
    setPhotoOpen(false);
    setFriendsOnlyHint(false);
    setMoreOpen(false);
    setConfirming(null);
    setBusy(false);
  }

  // Подсказка живёт несколько секунд: она объясняет одно касание, а не остаётся строкой карточки.
  useEffect(() => {
    if (!friendsOnlyHint) return;
    const timer = setTimeout(() => setFriendsOnlyHint(false), 4000);
    return () => clearTimeout(timer);
  }, [friendsOnlyHint]);

  const themed = useMemo(
    () => ({
      overlay: modalOverlayRgba(visualTheme),
      cardBg: loginChrome.cardBg,
      titleColor: loginChrome.titleColor,
      nameColor: loginChrome.titleColor,
      subColor: loginChrome.subColor,
      inputText: loginChrome.inputText,
      closeBg: loginChrome.inputBg,
      closeBorder: loginChrome.inputBorder,
      closeText: loginChrome.titleColor,
      divider: visualTheme === 'dark' ? '#27272a' : '#e5e7eb',
      handleColor: visualTheme === 'dark' ? '#4ade80' : '#059669',
      shadowOpacity: visualTheme === 'neutral' ? 0.22 : 0.45,
      primaryBg: visualTheme === 'dark' ? '#4ade80' : '#059669',
      primaryText: '#fff',
    }),
    [loginChrome, visualTheme],
  );

  if (!target) return null;
  if (presentation === 'overlay' && !visible) return null;

  const hasExtra = target.statusText || target.bio || target.publicHandle;

  const revision = target.revision ?? 0;
  /** Есть что открывать: бейдж рисует буквы, а не снимок, ровно при этих же условиях. */
  const hasPhoto = target.hasAvatar !== false && revision > 0;
  const canOpenPhoto = hasPhoto && target.canViewPhoto === true;

  const genderLabel =
    target.gender === 'MALE'
      ? t('genderMale')
      : target.gender === 'FEMALE'
        ? t('genderFemale')
        : null;
  const ageLabel =
    typeof target.age === 'number' ? `${target.age} ${t('profileYearsSuffix')}` : null;
  // День рождения без года — ровно то, что отдаёт сервер чужим: праздник виден, анкетные
  // данные нет.
  const birthdayLabel =
    typeof target.birthDay === 'number' && typeof target.birthMonth === 'number'
      ? `🎂 ${String(target.birthDay).padStart(2, '0')}.${String(target.birthMonth).padStart(2, '0')}`
      : null;
  const personLine = [genderLabel, ageLabel, birthdayLabel].filter(Boolean).join(' · ');

  const secondaryBtn = {
    backgroundColor: themed.closeBg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themed.closeBorder,
  };
  /** Содержимое «⋯». Разрушающее — последним и красным, как в любом системном меню. */
  const moreItems: { key: string; label: string; danger?: boolean; onPress: () => void }[] = [];
  if (target.onShareLink) {
    moreItems.push({
      key: 'share-link',
      label: t('profileShareLink'),
      onPress: () => {
        onClose();
        target.onShareLink?.();
      },
    });
  }
  if (target.onShareContact) {
    moreItems.push({
      key: 'share-contact',
      label: t('contactShareAction'),
      onPress: () => {
        onClose();
        target.onShareContact?.();
      },
    });
  }
  if (target.onRemoveContact) {
    moreItems.push({ key: 'remove', label: t('profileRemoveContact'), danger: true, onPress: () => setConfirming('remove') });
  }
  if (target.onBlock) {
    moreItems.push({ key: 'block', label: t('blockAction'), danger: true, onPress: () => setConfirming('block') });
  }

  const body = (
    <View
      style={[
        styles.overlay,
        { paddingTop: insets.top + 12, paddingBottom: insets.bottom + 12, backgroundColor: themed.overlay },
      ]}>
      <Pressable style={StyleSheet.absoluteFillObject} onPress={onClose} accessibilityLabel={t('close')} />
      <View
        style={[
          styles.card,
          {
            backgroundColor: themed.cardBg,
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: loginChrome.inputBorder,
            shadowOpacity: themed.shadowOpacity,
          },
        ]}
        pointerEvents="box-none">
        <Text style={[styles.title, { color: themed.titleColor }]}>{t('userCardTitle')}</Text>

        {/*
          Касание по фото — как в любом мессенджере: снимок раскрывается во весь экран.
          Незнакомцу нажатие не отказывает молча (кнопка, которая «не работает», читается
          как поломка), а один раз объясняет правило.
        */}
        <Pressable
          style={styles.avatarWrap}
          onPress={
            hasPhoto
              ? canOpenPhoto
                ? () => setPhotoOpen(true)
                : () => setFriendsOnlyHint(true)
              : undefined
          }
          disabled={!hasPhoto}
          hitSlop={6}
          accessibilityRole={hasPhoto ? 'button' : undefined}
          // Незрячему обещать «Открыть фото» там, где откроется отказ, нельзя: подпись
          // говорит то же, что и подсказка под аватаром.
          accessibilityLabel={
            !hasPhoto ? undefined : canOpenPhoto ? t('userCardOpenPhoto') : t('userCardPhotoFriendsOnly')
          }>
          <AvatarBadge
            userId={target.userId}
            displayName={target.displayName}
            hasAvatar={target.hasAvatar}
            revision={revision}
            shapeId={target.shapeId}
            size={120}
          />
        </Pressable>

        {friendsOnlyHint && !canOpenPhoto ? (
          <Text style={[styles.friendsOnlyHint, { color: themed.subColor }]}>
            {t('userCardPhotoFriendsOnly')}
          </Text>
        ) : null}

        <Text style={[styles.name, { color: themed.nameColor }]} numberOfLines={2}>
          {target.displayName}
        </Text>

        {target.publicHandle ? (
          <Text style={[styles.handle, { color: themed.handleColor }]}>#{target.publicHandle}</Text>
        ) : null}

        {target.statusText ? (
          <Text style={[styles.statusText, { color: themed.subColor }]} numberOfLines={2}>
            {target.statusText}
          </Text>
        ) : null}

        {/* Пол, возраст и дата — одной строкой под статусом. Каждая часть появляется только
            если заполнена: три подписи «не указано» подряд рассказывают лишь о том, что
            человек ничего не заполнял. */}
        {personLine ? (
          <Text style={[styles.statusText, { color: themed.subColor }]}>{personLine}</Text>
        ) : null}
        {target.relationNote ? (
          <Text style={[styles.statusText, { color: themed.subColor }]} accessibilityLiveRegion="polite">
            {target.relationNote}
          </Text>
        ) : null}

        {hasExtra && target.bio ? (
          <View style={[styles.bioBox, { borderTopColor: themed.divider }]}>
            <ScrollView style={{ maxHeight: 90 }} showsVerticalScrollIndicator={false}>
              <Text style={[styles.bioText, { color: themed.inputText }]}>{target.bio}</Text>
            </ScrollView>
          </View>
        ) : null}

        <View style={[styles.actions, { borderTopColor: themed.divider }]}>
          {confirming ? (
            <View style={styles.confirmBox}>
              <Text style={[styles.confirmTitle, { color: themed.titleColor }]}>
                {confirming === 'remove'
                  ? fillTemplate(t('profileRemoveConfirmTitle'), { name: target.displayName })
                  : t('blockConfirmTitle')}
              </Text>
              <Text style={[styles.confirmHint, { color: themed.subColor }]}>
                {confirming === 'remove' ? t('contactRemoveConfirmBody') : t('blockConfirmHint')}
              </Text>
              <View style={styles.confirmRow}>
                <Pressable
                  style={[styles.actionBtn, styles.confirmBtn, secondaryBtn]}
                  onPress={() => setConfirming(null)}
                  disabled={busy}
                  accessibilityRole="button">
                  <Text style={[styles.actionBtnText, { color: themed.closeText }]}>{t('cancel')}</Text>
                </Pressable>
                <Pressable
                  style={[styles.actionBtn, styles.confirmBtn, { backgroundColor: DANGER }]}
                  onPress={() => {
                    const run = confirming === 'remove' ? target.onRemoveContact : target.onBlock;
                    if (!run) return;
                    setBusy(true);
                    // Карточку закрываем после ответа сервера: закрытая сразу, она не смогла бы
                    // показать, что ничего не вышло, а человек решил бы, что всё получилось.
                    void Promise.resolve(run())
                      .then(() => onClose())
                      .catch(() => {
                        /* ошибку показывает вызывающий, карточка остаётся открытой */
                      })
                      .finally(() => {
                        setBusy(false);
                        setConfirming(null);
                      });
                  }}
                  disabled={busy}
                  accessibilityRole="button"
                  accessibilityLabel={confirming === 'remove' ? t('profileRemoveContact') : t('blockAction')}>
                  <Text style={[styles.actionBtnText, { color: '#fff' }]}>
                    {confirming === 'remove' ? t('profileRemoveContact') : t('blockAction')}
                  </Text>
                </Pressable>
              </View>
            </View>
          ) : null}

          {!confirming && (target.onWriteMessage || target.onCall || target.onVideoCall) ? (
            /*
              Три главных действия — одним рядом круглых кнопок, как в профиле любого
              мессенджера: до звонка другу раньше было три экрана (аудит 2026-10-04).
            */
            <View style={styles.quickRow}>
              {target.onWriteMessage ? (
                <QuickAction
                  label={t('profileMessage')}
                  color={themed.primaryBg}
                  onPress={() => {
                    onClose();
                    target.onWriteMessage?.();
                  }}>
                  <ComposeGlyph color="#fff" size={22} />
                </QuickAction>
              ) : null}
              {target.onCall ? (
                <QuickAction
                  label={t('profileCall')}
                  color={themed.primaryBg}
                  onPress={() => {
                    onClose();
                    target.onCall?.();
                  }}>
                  <TintedIcon source={PHONE_ICON} tintColor="#fff" style={styles.quickIcon} />
                </QuickAction>
              ) : null}
              {target.onVideoCall ? (
                <QuickAction
                  label={t('profileVideo')}
                  color={themed.primaryBg}
                  onPress={() => {
                    onClose();
                    target.onVideoCall?.();
                  }}>
                  <VideoCameraGlyph color="#fff" size={22} />
                </QuickAction>
              ) : null}
            </View>
          ) : null}

          {!confirming && target.onAddContact ? (
            <Pressable
              style={[
                styles.actionBtn,
                target.contactPending
                  ? { backgroundColor: themed.closeBg, borderWidth: StyleSheet.hairlineWidth, borderColor: themed.closeBorder }
                  : { backgroundColor: themed.primaryBg },
              ]}
              onPress={
                target.contactPending
                  ? undefined
                  : () => {
                      onClose();
                      target.onAddContact?.();
                    }
              }
              disabled={target.contactPending}
              accessibilityRole="button">
              <Text
                style={[
                  styles.actionBtnText,
                  { color: target.contactPending ? themed.subColor : themed.primaryText },
                ]}>
                {target.contactPending ? t('userCardContactPending') : t('userCardAddContact')}
              </Text>
            </Pressable>
          ) : null}

          {!confirming && moreOpen
            ? moreItems.map((item) => (
                <Pressable
                  key={item.key}
                  style={[styles.actionBtn, secondaryBtn]}
                  onPress={item.onPress}
                  accessibilityRole="button">
                  <Text style={[styles.actionBtnText, { color: item.danger ? DANGER : themed.closeText }]}>
                    {item.label}
                  </Text>
                </Pressable>
              ))
            : null}

          {!confirming ? (
            <View style={styles.bottomRow}>
              {moreItems.length > 0 ? (
                <Pressable
                  style={[styles.actionBtn, styles.moreBtn, secondaryBtn]}
                  onPress={() => setMoreOpen((v) => !v)}
                  accessibilityRole="button"
                  accessibilityState={{ expanded: moreOpen }}
                  accessibilityLabel={t('profileMore')}>
                  <MoreGlyph color={themed.closeText} />
                </Pressable>
              ) : null}
              <Pressable
                style={[styles.actionBtn, styles.closeBtn, secondaryBtn]}
                onPress={onClose}
                accessibilityRole="button">
                <Text style={[styles.actionBtnText, { color: themed.closeText }]}>{t('close')}</Text>
              </Pressable>
            </View>
          ) : null}
        </View>
      </View>

      {/* Слой поверх карточки — вторым <Modal> его открывать нельзя, см. `presentation`. */}
      {canOpenPhoto ? (
        <UserPhotoViewer
          visible={photoOpen}
          userId={target.userId}
          displayName={target.displayName}
          revision={revision}
          // Та же форма, что и у бейджа выше: крупный просмотр — это тот же аватар ближе,
          // а не другая картинка.
          shapeId={target.shapeId}
          onClose={() => setPhotoOpen(false)}
        />
      ) : null}
    </View>
  );

  if (presentation === 'overlay') {
    return <View style={[StyleSheet.absoluteFillObject, styles.overlayLayer]}>{body}</View>;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      // Аппаратная «назад» закрывает сначала фото, а не всю карточку разом: открытый
      // снимок — верхний слой, и уходить должен он.
      onRequestClose={() => {
        if (photoOpen) {
          setPhotoOpen(false);
          return;
        }
        onClose();
      }}>
      {body}
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  overlayLayer: { zIndex: 20 },
  card: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 16,
    paddingVertical: 20,
    paddingHorizontal: 18,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowRadius: 16,
    elevation: 8,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    marginBottom: 14,
  },
  avatarWrap: {
    marginBottom: 12,
  },
  friendsOnlyHint: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: -6,
    marginBottom: 8,
    paddingHorizontal: 8,
  },
  name: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 4,
  },
  handle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
  },
  statusText: {
    fontSize: 14,
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 8,
    paddingHorizontal: 4,
  },
  bioBox: {
    alignSelf: 'stretch',
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: 10,
    marginTop: 4,
    marginBottom: 4,
  },
  bioText: {
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  actions: {
    alignSelf: 'stretch',
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: 14,
    marginTop: 10,
    gap: 8,
  },
  // Карточка открывается в том числе с вкладки «Фото» в настройках, поэтому её кнопки
  // собраны из той же системы (`controlMetrics`), что кнопки настроек и списка чатов.
  // Базовая рамка снята: залитым кнопкам контур даёт заливка, а «вторичные» варианты
  // ставят себе `hairlineWidth` на месте вызова — как и сама карточка, у неё вся
  // обводка волосяная, и рамка в 1 px рядом с ней смотрелась бы жирной.
  actionBtn: {
    ...controlPill,
    borderWidth: 0,
  },
  actionBtnText: { ...controlTextM },
  quickRow: { flexDirection: 'row', justifyContent: 'center', gap: 18, marginBottom: 4 },
  quickAction: { alignItems: 'center', minWidth: 64, gap: 6 },
  quickCircle: { width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center' },
  quickIcon: { width: 22, height: 22 },
  quickLabel: { fontSize: 12, fontWeight: '600' },
  bottomRow: { flexDirection: 'row', gap: 8 },
  moreBtn: { width: 52, minHeight: 44, paddingHorizontal: 0 },
  closeBtn: { flex: 1, minHeight: 44 },
  confirmBox: { gap: 8 },
  confirmTitle: { fontSize: 16, fontWeight: '700', textAlign: 'center' },
  confirmHint: { fontSize: 13, lineHeight: 18, textAlign: 'center' },
  confirmRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
  confirmBtn: { flex: 1, minHeight: 44 },
});
